"use client";

import {
    getAdminCourseMetadataApiV1AdminCoursesCourseIdMetadataGet,
    queueReviewProactiveGenerationApiV1AdminReviewLessonItemUuidGenerateProactivePost,
} from "@/src/api/sdk.gen";
import { formatApiErrorMessage } from "@/src/lib/error-utils";
import { Fragment, useCallback, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Sparkles } from "lucide-react";

type MetadataRow = { id: number; label: string; type: string };

export interface GenerateProactiveDialogProps {
    isOpen: boolean;
    onClose: () => void;
    /** Numeric course id (from review item) */
    courseId: number;
    /** Generated lesson UUID for review */
    itemUuid: string;
    /** After task is queued; parent polls preview by labels */
    onQueued: (selection: { domainLabel: string; futureGoalLabel: string }) => void;
    onToast?: (message: string, type: "success" | "error") => void;
}

export default function GenerateProactiveDialog({
    isOpen,
    onClose,
    courseId,
    itemUuid,
    onQueued,
    onToast,
}: GenerateProactiveDialogProps) {
    const [domains, setDomains] = useState<MetadataRow[]>([]);
    const [goals, setGoals] = useState<MetadataRow[]>([]);
    const [departments, setDepartments] = useState<MetadataRow[]>([]);
    const [requireUserFutureGoal, setRequireUserFutureGoal] = useState(true);
    const [domainId, setDomainId] = useState<number | "">("");
    const [futureGoalId, setFutureGoalId] = useState<number | "">("");
    const [departmentId, setDepartmentId] = useState<number | "">("");
    const [loadingMeta, setLoadingMeta] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setError(null);
        setDomainId("");
        setFutureGoalId("");
        setDepartmentId("");

        const load = async () => {
            setLoadingMeta(true);
            try {
                const { data, error: reqErr } = await getAdminCourseMetadataApiV1AdminCoursesCourseIdMetadataGet({
                    path: { course_id: courseId },
                });
                const resData = (data || {}) as any;
                if (reqErr) {
                    if (!cancelled) setError(resData.error ?? "Failed to load course metadata");
                    return;
                }
                if (!cancelled) {
                    setDomains(Array.isArray(resData.domains) ? resData.domains : []);
                    setGoals(Array.isArray(resData.goals) ? resData.goals : []);
                    setDepartments(Array.isArray(resData.departments) ? resData.departments : []);
                    setRequireUserFutureGoal(resData.requireUserFutureGoal !== false);
                }
            } catch {
                if (!cancelled) setError("Failed to load course metadata");
            } finally {
                if (!cancelled) setLoadingMeta(false);
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [isOpen, courseId]);

    const handleClose = useCallback(() => {
        if (!submitting) onClose();
    }, [submitting, onClose]);

    const handleSubmit = async () => {
        if (domainId === "") {
            setError("Select a domain.");
            return;
        }
        if (requireUserFutureGoal && futureGoalId === "") {
            setError("Select a future goal.");
            return;
        }
        setError(null);
        setSubmitting(true);
        try {
            const dLabel = domains.find((d) => d.id === domainId)?.label ?? "";
            const gLabel =
                futureGoalId === "" ? "" : (goals.find((g) => g.id === futureGoalId)?.label ?? "");
            const depLabel = departmentId !== "" ? (departments.find((d) => d.id === departmentId)?.label ?? "") : "";
            const { data, error: reqErr } = await queueReviewProactiveGenerationApiV1AdminReviewLessonItemUuidGenerateProactivePost({
                path: { item_uuid: itemUuid },
                body: {
                    domainLabel: dLabel,
                    ...(gLabel ? { futureGoalLabel: gLabel } : {}),
                    ...(depLabel ? { departmentLabel: depLabel } : {}),
                },
            });
            const resData = (data || {}) as any;
            if (reqErr) {
                const msg = formatApiErrorMessage(reqErr, "Failed to queue proactive generation");
                setError(msg);
                onToast?.(msg, "error");
                return;
            }
            onQueued({ domainLabel: dLabel, futureGoalLabel: gLabel });
            onToast?.(resData.message ?? "Proactive generation queued.", "success");
            onClose();
        } catch (e) {
            const msg = formatApiErrorMessage(e, "Request failed");
            setError(msg);
            onToast?.(msg, "error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md rounded-xl bg-card border border-border shadow-xl p-6">
                                <div className="flex items-center gap-2 mb-1">
                                    <Sparkles className="w-5 h-5 text-indigo-600" />
                                    <Dialog.Title className="text-lg font-semibold text-foreground">
                                        Generate proactive lesson
                                    </Dialog.Title>
                                </div>
                                <p className="text-sm text-muted-foreground mb-4">
                                    {requireUserFutureGoal
                                        ? "Choose the user domain and future goal for this course. The worker will create a proactive variant tied to your account for preview on this page."
                                        : "Choose the user domain for this course (future goal is optional). The worker will create a proactive variant tied to your account for preview on this page."}
                                </p>

                                {loadingMeta ? (
                                    <p className="text-sm text-muted-foreground py-4">Loading options…</p>
                                ) : (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                                                User domain
                                            </label>
                                            <select
                                                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                                                value={domainId === "" ? "" : String(domainId)}
                                                onChange={(e) =>
                                                    setDomainId(e.target.value ? Number(e.target.value) : "")
                                                }
                                                disabled={submitting || domains.length === 0}
                                            >
                                                <option value="">Select domain…</option>
                                                {domains.map((d) => (
                                                    <option key={d.id} value={d.id}>
                                                        {d.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {(requireUserFutureGoal || goals.length > 0) && (
                                            <div>
                                                <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                                                    User future goal
                                                    {!requireUserFutureGoal && (
                                                        <span className="font-normal normal-case text-muted-foreground">
                                                            {" "}
                                                            (optional)
                                                        </span>
                                                    )}
                                                </label>
                                                <select
                                                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                                                    value={futureGoalId === "" ? "" : String(futureGoalId)}
                                                    onChange={(e) =>
                                                        setFutureGoalId(e.target.value ? Number(e.target.value) : "")
                                                    }
                                                    disabled={submitting || goals.length === 0}
                                                    required={requireUserFutureGoal && goals.length > 0}
                                                >
                                                    <option value="">
                                                        {goals.length === 0 ? "No goals configured" : "Select goal…"}
                                                    </option>
                                                    {goals.map((g) => (
                                                        <option key={g.id} value={g.id}>
                                                            {g.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                        {departments.length > 0 && (
                                            <div>
                                                <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                                                    User department
                                                </label>
                                                <select
                                                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                                                    value={departmentId === "" ? "" : String(departmentId)}
                                                    onChange={(e) =>
                                                        setDepartmentId(e.target.value ? Number(e.target.value) : "")
                                                    }
                                                    disabled={submitting}
                                                >
                                                    <option value="">Select department…</option>
                                                    {departments.map((d) => (
                                                        <option key={d.id} value={d.id}>
                                                            {d.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {domains.length === 0 && !loadingMeta && (
                                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                                        No domains configured for this course. Add them on the course admin page
                                        first.
                                    </p>
                                )}
                                {domains.length > 0 &&
                                    requireUserFutureGoal &&
                                    goals.length === 0 &&
                                    !loadingMeta && (
                                        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                                            This course requires a future goal, but none are configured. Add future
                                            goals on the course admin page first.
                                        </p>
                                    )}

                                {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

                                <div className="mt-6 flex justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        disabled={submitting}
                                        className="px-4 py-2 text-sm font-medium rounded-lg border border-border hover:bg-muted disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleSubmit()}
                                        disabled={
                                            submitting ||
                                            loadingMeta ||
                                            domainId === "" ||
                                            domains.length === 0 ||
                                            (requireUserFutureGoal &&
                                                (futureGoalId === "" || goals.length === 0))
                                        }
                                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                                    >
                                        {submitting ? (
                                            "Queueing…"
                                        ) : (
                                            <>
                                                <Sparkles className="w-4 h-4" />
                                                Generate
                                            </>
                                        )}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
